/**
 * The ZIvapp welcome illustration.
 *
 * A ring of single-stroke doodles around a phone, in the spirit of
 * docs/references/01-doodle-circle.png — but drawn from scratch, because that
 * reference is WhatsApp's artwork and this is the first thing every ZIvapp
 * user sees. Original line art means no licence terms attached to the
 * product's primary brand asset.
 *
 * Why SVG rather than a Lottie or a video:
 *
 *   - It is one small file with no runtime player, and it scales to any screen
 *   - Every stroke inherits currentColor, so re-theming is one CSS variable
 *   - The self-drawing effect is free: a stroke can be told to reveal only the
 *     first N% of itself, and animating N from 0 to 100 draws it on
 *
 * Every path carries pathLength="1", which normalises its length to 1 unit
 * regardless of its real geometry. Without it each stroke would need its own
 * dash length and they would all draw at different speeds.
 *
 * The geometry lives in zivDoodles.data.js so it stays free of React and can
 * be rendered without a browser.
 */

import {
  ICONS, PLACEMENT, SPECKS, PHONE, PHONE_MARK, VIEWBOX, at, placementTransform
} from '@zivapp/shared/brand'
import './ZivDoodles.css'

/**
 * @param {object}  props
 * @param {number} [props.size]     rendered width/height in px
 * @param {boolean}[props.animate]  false renders it fully drawn, no animation
 */
export default function ZivDoodles ({ size = 300, animate = true, ...rest }) {
  // Strokes are numbered in render order so the draw-on reads as one sweep:
  // the phone first, then the ring filling in around it.
  let strokeIndex = 0

  return (
    <svg
      className={`zd${animate ? ' zd--animate' : ''}`}
      width={size}
      height={size}
      viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label="ZIvapp"
      {...rest}
    >
      <g className="zd-phone" strokeWidth="2">
        {PHONE.map(d => (
          <path key={d} d={d} pathLength="1" className="zd-stroke"
            style={{ '--i': strokeIndex++ }} />
        ))}
        <path
          d={PHONE_MARK}
          pathLength="1"
          className="zd-stroke"
          strokeWidth="2.4"
          style={{ '--i': strokeIndex++ }}
        />
      </g>

      {PLACEMENT.map((spot, i) => (
        <g
          key={`${spot.icon}-${i}`}
          className="zd-icon"
          style={{ '--i': i, '--float-delay': `${(i % 7) * 420}ms` }}
          transform={placementTransform(spot)}
        >
          {ICONS[spot.icon].map((d, j) => (
            <path key={j} d={d} pathLength="1" className="zd-stroke"
              style={{ '--i': strokeIndex++ }} />
          ))}
        </g>
      ))}

      {SPECKS.map(({ a, r, d }, i) => {
        const { x, y } = at(a, r)
        return (
          <circle
            key={i}
            className="zd-speck"
            style={{ '--i': i }}
            cx={x.toFixed(1)}
            cy={y.toFixed(1)}
            r={d}
          />
        )
      })}
    </svg>
  )
}
